import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Ho4ArticleSidebar from '../components/Ho4ArticleSidebar';
import { HO4_LAST_MODIFIED } from '../data/ho4Articles';

function Ho4LossOfUseArticlePage() {
  const navigate = useNavigate();
  const articleLinkStyle = { color: '#065F46', fontWeight: 600, textDecoration: 'underline' };

  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? '';

    document.title = 'What Is Loss of Use (Additional Living Expenses) on Renters Insurance? | GetSkimp';

    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        'content',
        'Learn what loss of use and additional living expenses (ALE) coverage on renters insurance pays for, typical limits, and when it applies after a covered loss.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta) descriptionMeta.setAttribute('content', previousDescription);
    };
  }, []);

  return (
    <div className="container">
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '2rem' }}>
        <Ho4ArticleSidebar
          activeHref="/knowledge-base/what-is-loss-of-use-renters-insurance"
          onNavigate={(href) => navigate(href)}
        />
        <section>
          <div style={{ marginBottom: '1rem' }}>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => navigate('/knowledge-base')} style={{ marginBottom: '1rem' }}>
              Back to self help library
            </button>
            <h2 style={{ marginBottom: '0.5rem' }}>What is loss of use (additional living expenses) on renters insurance?</h2>
            <p style={{ marginBottom: 0, color: '#4B5563' }}>
              Loss of use coverage — also called additional living expenses (ALE) — helps pay extra costs when a covered
              event makes your rental temporarily uninhabitable. It is a standard part of most HO-4 policies.
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6B7280' }}>Last modified: {HO4_LAST_MODIFIED}</p>
          </div>

          <article className="card mb-4">
            <div className="card-body">
              <h3 style={{ marginBottom: '0.75rem' }}>Quick answer</h3>
              <p>
                If a covered peril — like a fire or burst pipe — forces you out of your apartment while repairs happen,
                loss of use coverage helps pay for <strong>extra living costs above your normal expenses</strong>, such
                as a hotel, temporary rental, meals, and storage. It does not replace your rent if you can still live
                in the unit, and it only applies to covered losses.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>What loss of use typically covers</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Hotel or short-term rental while your unit is being repaired.</li>
                <li style={{ marginBottom: '0.5rem' }}>Increased food costs if you cannot cook at home (restaurant meals above your normal grocery budget).</li>
                <li style={{ marginBottom: '0.5rem' }}>Storage unit fees for belongings during displacement.</li>
                <li style={{ marginBottom: '0.5rem' }}>Laundry costs if you lose access to in-unit washer/dryer.</li>
                <li>Reasonable transportation increases tied to temporary relocation.</li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>How limits work on HO-4 policies</h3>
              <p>
                Loss of use is usually Coverage D on an HO-4 policy. Many policies set the limit as a{' '}
                <strong>percentage of your personal property limit</strong> — commonly 20% to 30%. For example, a $30,000
                personal property limit might include $6,000 to $9,000 in loss of use coverage.
              </p>
              <ul style={{ paddingLeft: '1.2rem', marginTop: '0.75rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Coverage lasts for a defined period or until your unit is habitable, whichever comes first.</li>
                <li style={{ marginBottom: '0.5rem' }}>You generally must show the expense is <em>additional</em> — paying $150/night for a hotel when your normal rent is $1,200/month, not the full hotel bill in isolation.</li>
                <li>Your landlord may still expect rent on a partially damaged unit depending on lease terms and local law — check your lease.</li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Real-world example</h3>
              <p>
                A kitchen fire in your building makes your unit uninhabitable for three weeks. Your renters insurance
                loss of use coverage may help pay for an extended-stay hotel ($2,400), extra dining costs ($350 above
                normal groceries), and a storage unit ($180) — as long as the total stays within your Coverage D limit
                and the fire is a covered peril.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>What loss of use does not cover</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Voluntary moves or renovations unrelated to a covered loss.</li>
                <li style={{ marginBottom: '0.5rem' }}>Displacement from floods (usually excluded — flood requires separate coverage).</li>
                <li style={{ marginBottom: '0.5rem' }}>Loss of income or missed work — that is not what ALE is designed for.</li>
                <li>Damage to the building structure itself — your landlord&apos;s policy handles that.</li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>How this fits with other HO-4 coverage</h3>
              <p style={{ marginBottom: 0 }}>
                Loss of use works alongside{' '}
                <Link to="/knowledge-base/how-much-personal-property-coverage-do-i-need-for-renters-insurance" style={articleLinkStyle}>personal property coverage</Link>{' '}
                (your belongings) and{' '}
                <Link to="/knowledge-base/how-much-liability-do-i-need-for-renters-insurance" style={articleLinkStyle}>liability coverage</Link>{' '}
                (if you caused damage to others). If you need to file a claim, see{' '}
                <Link to="/knowledge-base/how-to-file-a-renters-insurance-claim" style={articleLinkStyle}>how to file a renters insurance claim</Link>.
                Wondering whether renters insurance is worth the cost for this kind of protection? Read{' '}
                <Link to="/knowledge-base/is-ho4-worth-it" style={articleLinkStyle}>is HO-4 worth it</Link>.
              </p>
            </div>
          </article>

          <section className="card mb-4" style={{ borderColor: 'var(--color-green)', background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)', boxShadow: 'var(--shadow-md)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#065F46' }}>Start renters insurance with GetSkimp</h3>
              <p style={{ marginBottom: '1rem' }}>Get HO-4 coverage that includes loss of use protection if you are displaced from your rental.</p>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/qualify')} style={{ fontWeight: 700, paddingInline: '1.4rem' }}>Get started now</button>
            </div>
          </section>
          <section className="card" style={{ borderColor: '#FBBF24', background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#92400E' }}>Questions about ALE limits on your policy?</h3>
              <p style={{ marginBottom: '1rem' }}>An agent can explain loss of use limits and how they connect to your other coverage.</p>
              <button type="button" className="btn btn-outline" onClick={() => navigate('/help')} style={{ borderColor: '#D97706', color: '#92400E', fontWeight: 700 }}>Speak to an agent</button>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}

export default Ho4LossOfUseArticlePage;
